// src/components/ChallengeDetailsModal.js
import React, { useEffect, useRef, useState } from "react";
import pb from "../lib/pb";
import { useAuth } from "../contexts/AuthContext";
import UploadChallengeModal from "./UploadChallengeModal";

/*
ChallengeDetailsModal
 - open, onClose, challengeId
 - stays fullscreen modal (no navigation)
 - shows media, title, creator, description
 - shows real-time metrics (views, accepts, votes)
 - shows list of remixes (accepts -> remix records) in realtime
 - supports "Remix / Accept" which opens UploadChallengeModal (re-uses existing uploader)
 - automatically marks a view when opened (debounced to avoid spam)
*/

export default function ChallengeDetailsModal({ open, onClose, challengeId }) {
  const { user } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [counters, setCounters] = useState({ views: 0, accepts: 0, votes: 0, aye: 0, no: 0 });
  const [remixes, setRemixes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openUploader, setOpenUploader] = useState(false);
  const viewedRef = useRef(false); // to prevent repeated view posts in same session
  const subsRef = useRef([]);

  // load challenge details
  const loadChallenge = async () => {
    if (!challengeId) return;
    try {
      const rec = await pb.collection("buzzing_challenges").getOne(challengeId, { expand: "author" }).catch(()=>null);
      setChallenge(rec);
    } catch (err) {
      console.error("loadChallenge", err);
    } finally {
      setLoading(false);
    }
  };

  // aggregate counters for this challenge only
  const aggregateForChallenge = async (id) => {
    if (!id) return;
    try {
       const [views, accepts, votes] = await Promise.all([
        pb.collection("challenge_views").getList(1, 200, { filter: `challengeId="${id}"` }).catch(() => ({ items: [] })),
        pb.collection("challenge_accepts").getList(1, 200, { filter: `challengeId="${id}"` }).catch(() => ({ items: [] })),
        pb.collection("challenge_votes").getList(1, 500, { filter: `challengeId="${id}"` }).catch(() => ({ items: [] })),
      ]);

      const aye = votes.items.filter(v => v.vote === "aye").length;
      const no = votes.items.filter(v => v.vote === "no").length;

      setCounters({
        views: views.items?.length || 0,
        accepts: accepts.items?.length || 0,
        votes: votes.items?.length || 0,
        aye,
        no
      });
    } catch (err) {
      console.error("aggregateForChallenge", err);
    }
  };

  // load remixes (accepted remix records) and expand their author
  const loadRemixes = async (id) => {
    try {
      const accepts = await pb.collection("challenge_accepts").getFullList({ filter: `challengeId="${id}"`, sort: "-created", expand: "remixRef,remixRef.author" }).catch(()=>[]);
      // some accepts reference remixId -> expand.remixRef will be the remix record
      const remixesList = accepts.map(a => ({
        acceptId: a.id,
        remix: a.expand?.remixRef || null,
        by: a.userId || null,
        createdAt: a.createdAt,
      }));
      setRemixes(remixesList);
    } catch (err) {
      console.error("loadRemixes", err);
    }
  };

  // mark a view (one-time per modal open) � create challenge_views record
  const markView = async (id) => {
    if (!id) return;
    if (viewedRef.current) return;
    viewedRef.current = true;
    try {
      await pb.collection("challenge_views").create({
        challengeId: id,
        userId: user?.id || null,
        createdAt: new Date().toISOString(),
      });
      // re-aggregate
      aggregateForChallenge(id);
    } catch (err) {
      console.error("markView", err);
    }
  };

  // subscribe to realtime changes for analytics to keep modal live-updated
  const setupSubscriptions = (id) => {
    // Clear previous
    subsRef.current.forEach(un => { try { un(); } catch {} });
    subsRef.current = [];

    if (!id) return;

    const subViews = pb.collection("challenge_views").subscribe(`challengeId="${id}"`, (e) => {
      // quick update: increment/decrement appropriately
      aggregateForChallenge(id);
    });
    const subAccepts = pb.collection("challenge_accepts").subscribe(`challengeId="${id}"`, (e) => {
      aggregateForChallenge(id);
      loadRemixes(id);
    });
    const subVotes = pb.collection("challenge_votes").subscribe(`challengeId="${id}"`, (e) => {
      aggregateForChallenge(id);
    });

    // also subscribe to the main challenge record updates so we show edits
    const subChallenge = pb.collection("buzzing_challenges").subscribe(id, (e) => {
      if (e.action === "update" || e.action === "create") {
        setChallenge(e.record);
      }
      if (e.action === "delete") {
        setChallenge(null);
      }
    });

    subsRef.current.push(subViews, subAccepts, subVotes, subChallenge);
  };

  useEffect(() => {
    if (!open) {
      // cleanup subscriptions when modal closes
      subsRef.current.forEach(un => { try { un(); } catch {} });
      subsRef.current = [];
      viewedRef.current = false;
      setChallenge(null);
      setRemixes([]);
      setCounters({ views: 0, accepts: 0, votes: 0, aye: 0, no: 0 });;
      setLoading(true);
      return;
    }

    // when modal opens
    (async () => {
      await loadChallenge();
      await aggregateForChallenge(challengeId);
      await loadRemixes(challengeId);
      setupSubscriptions(challengeId);
      // mark view after loading
      markView(challengeId);
    })();

    // cleanup on unmount handled above at close
    
  }, [open, challengeId]);

  // When a remix is posted using the uploader, we must create a challenge_accepts record
  // The uploader (UploadChallengeModal) returns created record => pass to handler
  const handleRemixPosted = async (createdRecord) => {
    try {
      if (!createdRecord || !challengeId) return;
      // create acceptance linking original challenge -> remix
      await pb.collection("challenge_accepts").create({
        challengeId: challengeId,
        remixId: createdRecord.id,
        userId: user?.id || null,
        createdAt: new Date().toISOString(),
      });
      // optionally create a vote or other metadata
      loadRemixes(challengeId);
      aggregateForChallenge(challengeId);
      setOpenUploader(false);
      alert("Remix posted and registered as acceptance.");
    } catch (err) {
      console.error("handleRemixPosted", err);
      alert("Failed to register remix acceptance.");
    }
  };

  if (!open) return null;

  const impact = counters.aye;
  const ayeRatio = counters.aye ? counters.views / counters.aye : 0;
  const noRatio = counters.no ? counters.views / counters.no : 0;
  const isPositive = ayeRatio >= noRatio;
  const delta = Math.abs(ayeRatio - noRatio).toFixed(1);

  const mediaUrl = (rec) => {
    if (!rec) return null;
    if (rec.media && typeof rec.media === "string") return pb.files.getURL(rec, rec.media);
    // if record has multiple media items (e.g., media array) adapt if needed
    if (rec.media && Array.isArray(rec.media) && rec.media[0]) return pb.files.getURL(rec, rec.media[0]);
    return null;
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 120000,
      background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20
    }}>
      <div style={{ width: "98%", maxWidth: 1100, height: "94%", background: "#0b0b0d", borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <div style={{ padding: 12, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: 8, background: "#111", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700 }}>
              {challenge?.expand?.author?.username?.[0]?.toUpperCase() || "?"}
            </div>
            <div>
              <div style={{ fontSize: 16, color: "#fff", fontWeight: 700 }}>{challenge?.title || (challenge?.content || "Untitled")}</div>
              <div style={{ fontSize: 13, color: "#9aa" }}>By {challenge?.expand?.author?.username || "Anon"}</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div style={{ color: "#9aa", fontSize: 13 }}>
              ?? {counters.views} � ? {counters.accepts} � ?? {counters.votes}
            </div>

            {/* ?? Impact Score (ADDED) */}
            <div style={{ textAlign: "right" }}>
              <div style={{ fontWeight: 600, color: "#fff" }}>
                Impact {impact}
              </div>
              <div style={{ color: isPositive ? "#2ecc71" : "#ff4d4f" }}>
                {isPositive ? "?" : "?"} {delta}
              </div>
            </div>
            
            <button onClick={() => setOpenUploader(true)} style={{ background: "#ff6ec4", color: "#fff", padding: "8px 12px", borderRadius: 8, border: "none" }}>
              Remix / Accept
            </button>
            <button onClick={() => { onClose && onClose(); }} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.04)", color: "#fff", padding: "8px 10px", borderRadius: 8 }}>
              Close
            </button>
          </div>
        </div>

        {/* Content area */}
        <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Left: media */}
          <div style={{ flex: 1, background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {loading ? <div style={{ color: "#9aa" }}>Loading...</div> : (
              <>
                {challenge?.mediaType === "Video" && mediaUrl(challenge) ? (
                  <video src={mediaUrl(challenge)} controls playsInline style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                ) : (challenge?.mediaType === "Image" && mediaUrl(challenge)) ? (
                  <img src={mediaUrl(challenge)} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                ) : (challenge?.mediaType === "Music" && mediaUrl(challenge)) ? (
                  <audio src={mediaUrl(challenge)} controls style={{ width: "90%" }} />
                ) : (
                  <div style={{ color: "#999" }}>{challenge?.content || "No media"}</div>
                )}
              </>
            )}
          </div>

          {/* Right: details & remixes */}
          <div style={{ width: 420, borderLeft: "1px solid rgba(255,255,255,0.03)", padding: 14, overflowY: "auto" }}>
            <div style={{ marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>{challenge?.title || "Challenge"}</h3>
              <div style={{ color: "#bbb", marginTop: 8 }}>{challenge?.description || challenge?.content || ""}</div>
            </div>

            <div style={{ marginTop: 10 }}>
              <h4 style={{ marginBottom: 8 }}>Remixes / Accepts</h4>
              {remixes.length === 0 ? (
                <div style={{ color: "#777" }}>No remixes yet</div>
              ) : (
                remixes.map((r) => {
                  const rem = r.remix;
                  const remMedia = rem ? (rem.media ? pb.files.getURL(rem, rem.media) : null) : null;
                  return (
                    <div key={r.acceptId} style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                      <div style={{ width: 60, height: 60, borderRadius: 8, overflow: "hidden", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {remMedia ? <img src={remMedia} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ color: "#999" }}>�</div>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: "#fff", fontSize: 14 }}>{rem?.title || rem?.content || "Remix"}</div>
                        <div style={{ color: "#9aa", fontSize: 13 }}>{rem?.expand?.author?.username || rem?.author || "Unknown"}</div>
                      </div>
                      <div>
                        <button onClick={() => {
                          // open remix directly in new window or use existing viewer � here we just open the remix record in new tab
                          if (rem) {
                            window.open(`/challenge/${rem.id}`, "_blank");
                          }
                        }} style={{ padding: "6px 8px", borderRadius: 6 }}>Open</button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <h4 style={{ marginBottom: 8 }}>Interact</h4>

              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                <button onClick={async () => {
                  // voting example: create challenge_votes
                  try {
                    await pb.collection("challenge_votes").create({
                      challengeId,
                      userId: user?.id || null,
                      vote: "aye",
                      createdAt: new Date().toISOString()
                    });
                    alert("Voted (aye)");
                  } catch (err) {
                    console.error("vote err", err);
                    alert("Failed to vote");
                  }
                }} style={{ padding: "8px 10px", borderRadius: 8 }}>Vote ??</button>

                <button onClick={async () => {
                  try {
                    await pb.collection("challenge_votes").create({
                      challengeId,
                      userId: user?.id || null,
                      vote: "no",
                      createdAt: new Date().toISOString()
                    });
                    alert("Voted (no)");
                  } catch (err) {
                    console.error("vote err", err);
                    alert("Failed to vote");
                  }
                }} style={{ padding: "8px 10px", borderRadius: 8 }}>Vote ??</button>
              </div>

              <div>
                <button onClick={() => { setOpenUploader(true); }} style={{ background: "#00c48c", color: "#071012", padding: "8px 12px", borderRadius: 8, border: "none" }}>
                  Remix / Create (opens uploader)
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* uploader modal for remixing */}
      {openUploader && (
        <UploadChallengeModal
          open={openUploader}
          initialMode={challenge?.mediaType === "Image" ? "image" : challenge?.mediaType === "Video" ? "video" : "image"}
          onClose={() => setOpenUploader(false)}
          onPosted={(rec) => handleRemixPosted(rec)}
        />
      )}
    </div>
  );
}